<script setup lang="ts" generic="T extends Record<string, any>">
import Tooltip from "~/components/Common/Tooltip.vue";
import TruncatedText from "~/components/Common/TruncatedText.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import type { IIndexHeader } from "~/utils/interfaces/table-interfaces";

withDefaults(
  defineProps<{
    headers: IIndexHeader[];
    rows: T[];
    trackBy?: keyof T;
    showTotal?: boolean;
    expandFirstColumn?: boolean;
  }>(),
  { expandFirstColumn: true },
);

defineSlots<{
  row(props: { row: T; index: number }): any;
  total(): any;
}>();
</script>

<template>
  <table
    class="simple_table w-full border-separate border-spacing-0 mt-4 border border-stroke-color rounded-lg"
    :class="{ 'simple_table--expand-first': expandFirstColumn, 'table-fixed': headers.some((h) => h.widthPx) }"
  >
    <thead>
      <tr>
        <th
          v-for="(header, idx) in headers"
          :key="`h-${idx}`"
          class="simple_table__th px-0.5 py-3 meta text-left bg-light-grey-color text-tertiary-color border-b border-stroke-color"
          :class="{
            'rounded-tl-lg pl-3': idx === 0,
            'rounded-tr-lg pr-3': idx === headers.length - 1,
          }"
          :style="{
            ...(header.minWidthPx ? { minWidth: header.minWidthPx + 'px' } : {}),
            ...(header.widthPx ? { width: header.widthPx + 'px' } : {}),
          }"
        >
          <div
            class="flex items-center gap-1 min-w-0"
            :class="{
              'justify-end': header.align === 'right',
              'justify-center': header.align === 'center',
            }"
          >
            <TruncatedText v-if="header.name" :text="header.name" :align="header.align" />
            <Tooltip v-if="header.tooltip" :position="'top-mid'" append-to-body class="shrink-0">
              <template #img>
                <SvgIcon name="info" class="w-3.5 h-3.5 text-tertiary-color" />
              </template>
              <template #content>
                <div class="flex max-w-[420px] gap-2 items-center">
                  <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
                  <span class="body_regular">{{ header.tooltip }}</span>
                </div>
              </template>
            </Tooltip>
          </div>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(row, idx) in rows" :key="trackBy ? String(row[trackBy]) : idx">
        <slot name="row" :row="row" :index="idx" />
      </tr>
      <tr v-if="showTotal" class="simple_table__total_row">
        <slot name="total" />
      </tr>
    </tbody>
  </table>
</template>

<style lang="scss" scoped>
.simple_table--expand-first .simple_table__th:first-child {
  @apply w-full;
}
:deep(tbody td) {
  @apply py-[6px] px-0.5 border-b border-stroke-color align-middle;
}
:deep(tbody tr:last-child td) {
  @apply border-b-0;
}
:deep(tbody tr:last-child td:first-child) {
  @apply rounded-bl-lg;
}
:deep(tbody tr:last-child td:last-child) {
  @apply rounded-br-lg;
}
:deep(tbody td:first-child) {
  @apply pl-3;
}
:deep(tbody td:last-child) {
  @apply pr-3;
}
// Cells with the helper class get full right padding to line up with the header text.
:deep(tbody td.cell-right) {
  @apply pr-3;
}
.simple_table__total_row :deep(td) {
  @apply border-b-0 pt-3;
}
</style>
